"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const bs_stepper_1 = __importDefault(require("bs-stepper"));
const YourInfoStep_1 = require("./steps/YourInfoStep");
const PlanStep_1 = require("./steps/PlanStep");
const ExtrasStep_1 = require("./steps/ExtrasStep");
const DetailsStep_1 = require("./steps/DetailsStep");
const ScheduleStep_1 = require("./steps/ScheduleStep");
const Utils_1 = require("./Utils");
const CaptchaHandler_1 = require("./CaptchaHandler");
const Attribution_1 = require("./Attribution");
// shared variables between steps
const STATE = {
    // on-load configuration values
    config: {
        devMode: false,
        inIframe: window.self !== window.top,
        urlParams: new URLSearchParams(window.location.search),
        autofill: false,
        interestOnly: false,
        customerHub: false,
        additionalParameters: {}
    },
    // references to elements + data in the main window
    frame: {
        stepper: null,
        stepperEl: null,
        containerEl: null,
    },
    cookies: {
        // rotates itself once the previous spine placed an order — see resolveProfileUuid()
        uuid: (0, Attribution_1.resolveProfileUuid)(),
        agent: (0, Utils_1.getCookie)('agent'),
    },
    // populated in init() via captureAttribution(); seeded empty so the type is satisfied
    attribution: { firstTouch: null, lastTouch: null, params: {} },
    steps: [],
    ugpsData: undefined,
    userInput: {}
};
/**
 * Initialize the Order Form.
 */
async function init() {
    // initialize stepper
    const stepperEl = document.getElementById('stepper');
    if (!stepperEl) {
        throw new Error(`Couldn't initialize form!`);
    }
    STATE.frame.stepper = new bs_stepper_1.default(stepperEl, {
        linear: false,
        animation: true,
    });
    // mode settings
    STATE.config.autofill = STATE.config.urlParams.get('autofill') === 'true';
    STATE.config.interestOnly = STATE.config.urlParams.get('interestOnly') === 'true';
    // abMode (?ab=B) is retired on /order2 — key kept so downstream branches stay dormant
    STATE.config.additionalParameters['abMode'] = false;
    // v4: capture marketing attribution (first/last touch cookies) + persist the profileUuid spine
    STATE.attribution = (0, Attribution_1.captureAttribution)(STATE.config.urlParams);
    (0, Attribution_1.persistProfileUuid)(STATE.cookies.uuid);
    // list of steps to instantiate (Plan/Extras split + merged Details, source-design style)
    let Steps = [
        YourInfoStep_1.YourInfoStep,
        PlanStep_1.PlanStep,
        ExtrasStep_1.ExtrasStep,
        DetailsStep_1.DetailsStep,
        ScheduleStep_1.ScheduleStep
    ];
    // are we embedded in the Customer Hub?
    if (STATE.config.urlParams.get('customerHub') === 'true') {
        console.log(`[OF] Customer Hub mode enabled.`);
        STATE.config.customerHub = true;
        STATE.config.additionalParameters['cid'] = STATE.config.urlParams.get('cid') || '';
        // NO hubToken. The session used to arrive here as a URL parameter, read out of
        // document.cookie by hub.js. /api/ugpsPlanChange reads the cookie server-side now, and
        // this form is same-origin with it, so nothing has to carry the JWT through page script.
        STATE.config.additionalParameters['currentRate'] = STATE.config.urlParams.get('currentRate') || '';
        // update the list of steps to instantiate
        Steps = [
            PlanStep_1.PlanStep,
            ExtrasStep_1.ExtrasStep,
            DetailsStep_1.DetailsStep
        ];
    }
    Steps.forEach(step => STATE.steps.push(new step(STATE)));
    // stepper event listeners
    stepperEl.addEventListener(`show.bs-stepper`, stepperStepChange);
    // alert dismissal (using event delegation)
    document.addEventListener(`click`, (e) => {
        const target = e.target;
        if (target && target.matches('.dismiss, .dismiss .fa, .dismiss .fa use')) {
            const alert = target.closest('.alert');
            alert?.classList.add('fade-out');
            setTimeout(() => alert?.remove(), 310);
        }
    });
    // send resize events if in an iframe
    let previousHeight = 0;
    STATE.frame.containerEl = document.getElementById('frame');
    function sendFullScreen() {
        let elHeight = STATE.frame.containerEl.offsetHeight + 30;
        if (previousHeight !== elHeight) {
            previousHeight = elHeight;
            window.parent.postMessage(JSON.stringify({
                'height': elHeight
            }), '*');
        }
    }
    if (STATE.config.inIframe) {
        window['autoResize'] = setInterval(sendFullScreen, 100);
    }
    if (!STATE.config.customerHub) {
        // event
        (0, Utils_1.sendGEvent)('loadedForm', STATE);
        (0, Utils_1.sendEvent)('loadedForm', STATE);
    }
}
/**
 * Handle step change events.
 * @param {StepperEvent} e - The stepper event object.
 */
function stepperStepChange(e) {
    let { from, to } = e.detail;
    // can we go to this step?
    if (STATE.steps[to].isLocked()) {
        e.preventDefault();
        // TODO: make some animation and change the element to `cursor: not-allowed`
    }
    else {
        STATE.frame.stepper._steps[to]?.classList?.remove('disabled');
        // v3 progress band state: the band hides while the address gate (step 0,
        // non-hub) is active, and every step before the active one reads as done
        const stepperEl = STATE.frame.stepperEl ?? document.getElementById('stepper');
        if (stepperEl) {
            stepperEl.classList.toggle('gate-active', !STATE.config.customerHub && to === 0);
        }
        STATE.frame.stepper._steps.forEach((stepEl, i) => {
            stepEl?.classList?.toggle('done', i < to && !stepEl.classList.contains('disabled'));
        });
        // show event
        STATE.steps[to].onShow();
    }
}
/**
 * Start the initialization process when the DOM is fully loaded.
 */
document.addEventListener('DOMContentLoaded', async () => {
    try {
        // expose STATE for debugging when URL has ?debug (the param itself — a
        // substring match fired on any link carrying "debug" in a value, e.g. ?utm_campaign=debug-test)
        if (STATE.config.urlParams.has('debug')) {
            STATE.config.devMode = true;
            if (STATE.config.urlParams.get('autofill') === 'true') {
                console.log(`[OF] Autofill mode enabled.`);
            }
            console.log(`Debug mode enabled - exposing STATE to window.STATE + unlocking all steps.`);
            // @ts-ignore
            window.STATE = STATE;
            // unlock all steps for easier testing
            STATE.steps.forEach(step => step.unlock());
        }
        await init();
    }
    catch (err) {
        console.log(`[OF] Error during init:`, err);
        // TODO: Show error to user?
    }
});
// inject into global scope for reCAPTCHA to find
window['captchaOnSuccess'] = CaptchaHandler_1.captchaOnSuccess;
window['captchaOnExpire'] = CaptchaHandler_1.captchaOnExpire;
window['captchaOnError'] = CaptchaHandler_1.captchaOnError;
